import { api } from '../../api/setup';

// Types
interface GraphNode {
  id: string;
  element_id: string;
  name: string;
  labels: string[];
  properties: Record<string, any>;
  n_iteration?: number;
  hs_code?: string[];
  hs_code_description?: string[];
  function?: string;
}

interface GraphRelationship {
  id: string;
  start_node_id: string;
  end_node_id: string;
  type: string;
  properties?: Record<string, any>;
}

interface IncomingRelationship {
  sourceNode: GraphNode | undefined;
  relationship: GraphRelationship;
  relationshipType: string;
  weight: number;
}

interface Country {
  code: string;
  name: string;
}

interface TradeYear {
  year: number;
}

interface HSCode {
  code: string;
  description: string;
}

interface TradeSummary {
  exports_to_world: any[];
  imports_from_world: any[];
}

interface RCAData {
  totals: {
    country_exports: number;
    world_exports: number;
  };
  items: any[];
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
}

export const hsCodeValueChainService = {
  // Removed metadata usage; call getCountries and getTradeYears directly

  async getCountries(): Promise<Country[]> {
    const data = await api.countries.listCountriesApiV1CountriesGet();
    return Array.isArray(data) ? data : [];
  },

  async getAvailableHSCodes(): Promise<GraphNode[]> {
    const resp: ApiResponse<GraphNode[]> = await api.graphData.getAvailableRootNodesApiV1GraphRootsGet();
    if (resp?.success && resp?.data) return resp.data;
    return [];
  },

  async getHSCodeValueChainData(elementId: string, maxIterations: number = 3, minThreshold?: number): Promise<any> {
    const resp: any = await api.graphData.getIncomingUnevenGraphApiV1GraphIncomingUnevenElementIdGet(String(elementId), maxIterations, minThreshold);
    if (resp?.success && resp?.data) return resp.data;
    throw new Error('Failed to fetch HS code value chain data');
  },

  async getHSCodeIncomingRelationships(elementId: string, minThreshold?: number): Promise<IncomingRelationship[]> {
    const resp: any = await api.graphData.getIncomingUnevenGraphApiV1GraphIncomingUnevenElementIdGet(String(elementId), 1, minThreshold);
    if (resp?.success && resp?.data) {
      const data = resp.data;
      const directIncoming = data.relationships
        .filter((rel: any) => rel.end_node_id === elementId)
        .map((rel: any) => {
          const sourceNode = data.nodes.find((node: any) => node.id === rel.start_node_id);
          return {
            sourceNode: sourceNode,
            relationship: rel,
            relationshipType: rel.type,
            weight: rel.properties?.path_weight || 1
          };
        });
      return directIncoming;
    }
    return [];
  },

  async getTradeYears(): Promise<TradeYear[]> {
    const data = await api.trade.listTradeYearsApiV1TradeYearsGet();
    return Array.isArray(data) ? data : [];
  },

  async getCountryWorldHSCodes(countryCode: string, year: number): Promise<HSCode[]> {
    const data = await api.trade.countryWorldHsCodesApiV1TradeCountryWorldHsCodesGet(Number(countryCode), Number(year));
    return Array.isArray(data) ? data : [];
  },

  async getCountryWorldSummary(countryCode: string, year: number, hsCodes: string[]): Promise<TradeSummary> {
    const codes = Array.isArray(hsCodes) && hsCodes.length > 0 ? hsCodes.join(',') : '';
    const data = await api.trade.countryWorldTradeSummaryApiV1TradeCountryWorldSummaryGet(Number(countryCode), Number(year), codes);
    return data || { exports_to_world: [], imports_from_world: [] };
  },

  async getRCA(countryCode: string, year: number, hsCodes: string[]): Promise<RCAData> {
    const codes = Array.isArray(hsCodes) && hsCodes.length > 0 ? hsCodes.join(',') : '';
    const data = await api.trade.rcaByHsApiV1TradeRcaGet(Number(countryCode), Number(year), codes);
    return data || { totals: { country_exports: 0, world_exports: 0 }, items: [] };
  }
};

export default hsCodeValueChainService;
